import { forwardRef, type InputHTMLAttributes } from "react";
import styles from "./PunchupToggle.module.css";

export interface PunchupToggleProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | "type"
  | "checked"
  | "id"
  | "name"
  | "disabled"
  | "onChange"
  | "className"
  | "style"
> {
  /** Controlled checked state. */
  isChecked: boolean;
  /** Field name — also the fallback for `id` and the input's accessible name. */
  name: string;
  /** Input id; defaults to `name`. */
  id?: string;
  disabled?: boolean;
  /** Native `change` handler on the underlying checkbox. */
  handleChange?: () => void;
  /** `click` handler — the controlled-toggle callback `PunchupInputToggle` drives state with. */
  handleClick?: () => void;
}

/**
 * PunchupToggle
 *
 * The base toggle switch: a hidden native `<input type="checkbox">` (for keyboard + form semantics) paired
 * with a custom `.track` + `.knob` the design system fully controls — native checkboxes ignore
 * border/border-radius/:hover, so the visual is a styled element driven by CSS sibling selectors off the
 * input's `:checked` / `:disabled` / `:focus-visible`. Pure presentational forwarder (no hooks) → no
 * `"use client"`; the interactive directive belongs on the consuming field's client boundary. Prefer
 * `PunchupFormToggle` for form contexts — this is the control `PunchupInputToggle` composes. Extra input
 * attributes (`role`, `aria-*`, a Slot trigger's props) spread onto the input, and the input overlays the
 * track so it can anchor a Radix `asChild` trigger.
 */
export const PunchupToggle = forwardRef<HTMLInputElement, PunchupToggleProps>(
  (
    {
      isChecked,
      name,
      id,
      disabled,
      handleChange,
      handleClick,
      onClick,
      ...inputProps
    },
    ref
  ) => {
    const inputId = id ?? name;
    return (
      <label
        htmlFor={inputId}
        className={styles.label}
        data-disabled={disabled || undefined}
      >
        <input
          aria-label={inputId}
          {...inputProps}
          ref={ref}
          id={inputId}
          name={name}
          type="checkbox"
          className={styles.input}
          onChange={handleChange}
          disabled={disabled}
          onClick={(event) => {
            onClick?.(event);
            handleClick?.();
          }}
          readOnly
          checked={isChecked}
        />
        <span className={styles.track}>
          <span className={styles.knob} />
        </span>
      </label>
    );
  }
);
PunchupToggle.displayName = "PunchupToggle";
